'use client';

import {
  ExperiencesListFilter,
  ExperiencesListMedia,
  GetExperiencesResponse,
} from '@/actions/experiences/types';
import { Card } from '@/components/Card';
import { ShortList } from '@/components/CategorySections/ExperienceType';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import useLocalStorage from '@/hooks/useLocalStorage';
import { toShortListMedia } from '@/utils/shortlistUtils';
import { usePathname } from 'next/navigation';
import React, { HTMLAttributes } from 'react';
import Slider from 'react-slick';
import './styles.css';

// Older next-admin deploys sent the cover filters as a flat list, without the
// media id, path and filename.
const getCoverMedia = (media: GetExperiencesResponse['media']) => {
  const medias = media.filter(
    (m): m is ExperiencesListMedia => 'mediaFilters' in m,
  );
  if (medias.length) return medias.find((m) => m.type === 'cover') ?? medias[0];

  const filters = media.filter(
    (m): m is ExperiencesListFilter => !('mediaFilters' in m),
  );
  return filters.length ? { mediaFilters: filters } : null;
};

interface IFavouritesProps extends HTMLAttributes<HTMLDivElement> {
  experiences: GetExperiencesResponse[];
}

export const Favourites: React.FC<IFavouritesProps> = ({ experiences }) => {
  const { experiences: dict } = useTranslation();
  const lang = useLocale();
  const [favourites, setFavourites] = useLocalStorage(
    'experience',
    [] as ShortList[],
  );

  const path = usePathname();
  const adjustedPath = lang !== 'en' ? path.slice(3) : path;

  const settings = {
    dots: true,
    infinite: true,
    speed: 1500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 750,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1100,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
    ],
  };

  const toggleFavorite = (exp: GetExperiencesResponse) => {
    const translation = exp.experienceTranslation.find((item) => {
      if (item.locale === lang) {
        return item;
      }
    }) as GetExperiencesResponse['experienceTranslation'][0];

    const titleSlugs = exp.experienceTranslation.map((item) => ({
      [item.locale]: item.slug,
      title: item.title,
    })) as { [key: string]: string }[];

    const isFavourite = favourites.some((item) => item.id === exp.id);

    const shortListInfos: ShortList = {
      id: exp.id,
      title: translation.title,
      titleSlugs: titleSlugs,
      categoryId: exp.macroCategory.id,
      media: toShortListMedia(getCoverMedia(exp.media)),
      zone: {
        code: exp.zoneCode ?? '',
      },
      price: Number(exp.price),
      currency: 'EUR',
      city: exp.city,
      duration: exp.duration,
      languagens: exp.languages,
      minGuests: exp.minParticipants,
      maxGuests: exp.maxParticipants,
    };

    if (isFavourite) {
      const updatedFavourites = favourites.filter((item) => item.id !== exp.id);
      setFavourites(updatedFavourites);
      localStorage.setItem('experience', JSON.stringify(updatedFavourites));
    } else {
      const updatedFavourites = [...favourites, shortListInfos];
      setFavourites(updatedFavourites);
      localStorage.setItem('experience', JSON.stringify(updatedFavourites));
    }
  };

  const FavouriteCard = experiences.map((experience, index) => {
    const translation = experience.experienceTranslation.find((item) => {
      if (item.locale === lang) {
        return item;
      }
    }) as GetExperiencesResponse['experienceTranslation'][0];

    const macroCategory = experience.macroCategory.translations.find((item) => {
      if (item.locale === lang) {
        return item;
      }
    });

    const isFavourite = favourites.some(
      (item: { id: number }) => item.id === experience.id,
    );

    return (
      <div key={index} className="desktop:flex-auto px-7 desktop:px-0">
        <Card
          key={index}
          cardMedia={{
            media: getCoverMedia(experience.media),
            filterName: 'vertical_slider_webp',
          }}
          hasFavouriteButton={true}
          onClickFavourite={() => toggleFavorite(experience)}
          isFavourite={isFavourite}
          href={`${adjustedPath}/${macroCategory!.slug}/${translation.slug}`}
          customStyle="flex-auto flex h-full w-auto before:h-[100%] before:bg-home-gradient mb-0 desktop:hover:mb-5 -mt-0 desktop:hover:-mt-5"
          sizes="(min-width: 640px) 50vw, (min-width: 800px) 35vw, (min-width: 1200px) 27vw, 100vw">
          <div className="flex flex-row items-center h-fit">
            <div className="flex flex-col flex-1">
              <p className="text-subtitle text-center w-full box-border whitespace-normal font-averta-semibold">
                <strong>{translation.title}</strong>
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  });

  return (
    <div>
      <h2 className="text-title font-title font-bold w-full text-center">
        {dict.favourites.title}
      </h2>
      <p className="my-5 text-center text-gray-850 text-lg">
        {dict.favourites.subtitle}
      </p>

      <div className="grid grid-cols-4 gap-6 desktop:mt-14 desktop:mb-[200px] h-0 invisible desktop:visible desktop:h-auto">
        {FavouriteCard}
      </div>

      <div className="flex justify-center w-full mt-14 mb-[200px] desktop:hidden">
        <div className="relative flex flex-col justify-center w-full">
          <Slider {...settings} className="favourite-carousel visible w-full">
            {FavouriteCard}
          </Slider>
        </div>
      </div>
    </div>
  );
};
